// ====================================================
// VidyaTrack — Admin: Teachers Management
// Creates real Firebase Auth + Firestore profiles
// ====================================================

import React, { useState, useEffect } from "react";
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiEye, FiEyeOff, FiMail, FiLock } from "react-icons/fi";
import { useData } from "../../contexts/DataContext";
import { useAuth } from "../../contexts/AuthContext";
import Modal from "../../components/shared/Modal";
import { toast } from "react-toastify";

const SUBJECTS = [
  "Mathematics","Science","English","Nepali","Social Studies",
  "Computer","Health","Optional Math","Account","Physics","Chemistry","Biology",
];

function generatePassword(name) {
  const first = (name || "Teacher").split(" ")[0];
  return first.charAt(0).toUpperCase() + first.slice(1,4).toLowerCase() + "2025@";
}

export default function TeachersPage() {
  const { getTeachers, getClasses, updateUser, deleteUser: dbDeleteUser } = useData();
  const { register } = useAuth();

  const [teachers,  setTeachers]  = useState([]);
  const [classes,   setClasses]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editTc,    setEditTc]    = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [showPw,    setShowPw]    = useState(false);
  const [errors,    setErrors]    = useState({});

  const blankForm = { name:"", email:"", password:"", subject:"", phone:"", classes:[] };
  const [form, setForm] = useState(blankForm);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [tc, cl] = await Promise.all([getTeachers(), getClasses()]);
      setTeachers(tc); setClasses(cl);
    } catch { toast.error("Failed to load teachers"); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditTc(null); setForm(blankForm); setErrors({}); setShowPw(false); setShowModal(true);
  };

  const openEdit = (t) => {
    setEditTc(t);
    setForm({ name:t.name||"", email:t.email||"", password:"", subject:t.subject||"", phone:t.phone||"", classes:t.classes||[] });
    setErrors({}); setShowPw(false); setShowModal(true);
  };

  const handleNameChange = (name) => {
    setForm(p => ({ ...p, name, password: editTc ? p.password : generatePassword(name) }));
    setErrors(p => ({ ...p, name:"" }));
  };

  const toggleClass = (label) => {
    setForm(p => ({
      ...p,
      classes: p.classes.includes(label) ? p.classes.filter(c => c !== label) : [...p.classes, label],
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())  e.name    = "Full name is required";
    if (!form.email.trim()) e.email   = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Enter a valid email";
    if (!editTc) {
      if (!form.password)          e.password = "Password is required";
      else if (form.password.length < 6) e.password = "Minimum 6 characters";
    }
    if (!form.subject) e.subject = "Please select a subject";
    return e;
  };

  const handleSave = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      if (editTc) {
        await updateUser(editTc.id, {
          name:    form.name.trim(),
          subject: form.subject,
          phone:   form.phone.trim(),
          classes: form.classes,
        });
        toast.success(`${form.name}'s profile updated!`);
      } else {
        await register({
          email:   form.email.trim().toLowerCase(),
          password:form.password,
          name:    form.name.trim(),
          role:    "teacher",
          subject: form.subject,
          phone:   form.phone.trim(),
          classes: form.classes,
        });
        toast.success(
          `✅ Teacher account created!\nEmail: ${form.email}\nPassword: ${form.password}`,
          { autoClose: 8000 }
        );
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.message);
      if (err.message?.includes("email")) setErrors(p => ({ ...p, email: err.message }));
    } finally { setSaving(false); }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete ${t.name}? Their Firestore profile will be removed.`)) return;
    try { await dbDeleteUser(t.id); toast.success("Teacher deleted!"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const f = (key, val) => { setForm(p => ({...p,[key]:val})); setErrors(p => ({...p,[key]:""})); };

  const filtered = teachers.filter(t =>
    (t.name||"").toLowerCase().includes(search.toLowerCase()) ||
    (t.email||"").toLowerCase().includes(search.toLowerCase()) ||
    (t.subject||"").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="animate-fade">
      <div className="page-head">
        <div>
          <h1 className="page-title">👩‍🏫 Teachers</h1>
          <p className="page-subtitle">{teachers.length} teacher{teachers.length !== 1 ? "s" : ""} on staff</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus size={15}/> Add Teacher</button>
      </div>

      <div style={{ marginBottom:16 }}>
        <div className="search-box" style={{ maxWidth:380 }}>
          <FiSearch className="search-icon" size={14}/>
          <input placeholder="Search by name, email, subject…" value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner"/></div>
      ) : (
        <div className="g g3">
          {filtered.map((t, i) => (
            <div key={t.id} className="card">
              <div style={{ padding:"18px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
                  <div className="avatar av-lg" style={{ background:`hsl(${i*60},65%,88%)`, color:`hsl(${i*60},65%,30%)`, fontWeight:700 }}>
                    {(t.name||"?").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:".93rem", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.name}</div>
                    <div style={{ fontSize:".74rem", color:"var(--gray-500)", marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.email}</div>
                  </div>
                </div>

                <div style={{ marginBottom:10 }}>
                  {t.subject && <span className="badge badge-blue" style={{ fontSize:".68rem", marginRight:5 }}>{t.subject}</span>}
                  {(t.classes||[]).map(c => <span key={c} className="badge badge-gray" style={{ fontSize:".65rem", marginRight:4, marginBottom:4 }}>{c}</span>)}
                </div>

                {t.phone && <div style={{ fontSize:".78rem", color:"var(--gray-500)", marginBottom:12 }}>📞 {t.phone}</div>}

                <div style={{ display:"flex", gap:8 }}>
                  <button className="btn btn-secondary btn-sm" style={{ flex:1 }} onClick={() => openEdit(t)}>
                    <FiEdit2 size={13}/> Edit
                  </button>
                  <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(t)} title="Delete">
                    <FiTrash2 size={13}/>
                  </button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="card" style={{ gridColumn:"1/-1" }}>
              <div className="empty-state">
                <div className="empty-icon">👩‍🏫</div>
                <div className="empty-title">{search ? "No teachers match your search" : "No teachers yet"}</div>
                <div className="empty-desc">Click "Add Teacher" to create a teacher account.</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Modal ── */}
      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={editTc ? `Edit — ${editTc.name}` : "Create Teacher Account"}
        size="lg"
        footer={<>
          <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <><div className="spinner spinner-sm"/> {editTc?"Saving…":"Creating…"}</> : editTc?"Save Changes":"Create Account"}
          </button>
        </>}
      >
        {!editTc && (
          <div style={{ background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:"var(--r8)", padding:"11px 14px", marginBottom:16, fontSize:".83rem", color:"#1d4ed8", display:"flex", gap:8 }}>
            <span>💡</span>
            <div>This creates a <strong>real login account</strong>. Share the email and password with the teacher so they can sign in.</div>
          </div>
        )}

        <div className="g g2">
          <div className="form-group" style={{ gridColumn:"1/-1" }}>
            <label className="form-label">Full Name *</label>
            <input className={`form-control${errors.name?" error":""}`} placeholder="Teacher's full name"
              value={form.name} onChange={e => handleNameChange(e.target.value)}/>
            {errors.name && <div className="form-error">{errors.name}</div>}
          </div>

          <div className="form-group">
            <label className="form-label"><FiMail size={12} style={{ display:"inline", marginRight:4 }}/>Email *</label>
            <input type="email" className={`form-control${errors.email?" error":""}`} placeholder="teacher@school.com"
              value={form.email}
              readOnly={!!editTc}
              style={editTc?{background:"var(--gray-100)",cursor:"not-allowed"}:{}}
              onChange={e => f("email", e.target.value)}/>
            {errors.email && <div className="form-error">{errors.email}</div>}
            {editTc && <div className="form-hint">Email cannot be changed after creation.</div>}
          </div>

          {!editTc && (
            <div className="form-group">
              <label className="form-label"><FiLock size={12} style={{ display:"inline", marginRight:4 }}/>Password *</label>
              <div className="input-wrap">
                <input type={showPw?"text":"password"} className={`form-control pr${errors.password?" error":""}`}
                  placeholder="Min. 6 characters" value={form.password} onChange={e => f("password", e.target.value)}/>
                <button type="button" className="icon-right" onClick={() => setShowPw(v=>!v)}
                  style={{ background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", color:"var(--gray-400)" }}>
                  {showPw ? <FiEyeOff size={14}/> : <FiEye size={14}/>}
                </button>
              </div>
              {errors.password && <div className="form-error">{errors.password}</div>}
              <div className="form-hint">Auto-generated: <strong>{form.password}</strong></div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Subject *</label>
            <select className={`form-control${errors.subject?" error":""}`} value={form.subject} onChange={e => f("subject", e.target.value)}>
              <option value="">Select subject</option>
              {SUBJECTS.map(s => <option key={s}>{s}</option>)}
            </select>
            {errors.subject && <div className="form-error">{errors.subject}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-control" placeholder="+977-98XXXXXXXX" value={form.phone} onChange={e => f("phone", e.target.value)}/>
          </div>
        </div>

        {classes.length > 0 && (
          <div className="form-group">
            <label className="form-label">Assign Classes</label>
            <div style={{ display:"flex", flexWrap:"wrap", gap:7 }}>
              {classes.map(c => {
                const label = `${c.name} - ${c.section}`;
                const sel   = form.classes.includes(label);
                return (
                  <button key={c.id} type="button" onClick={() => toggleClass(label)} style={{
                    padding:"5px 13px", borderRadius:20, cursor:"pointer",
                    border:`1.5px solid ${sel?"var(--blue-700)":"var(--border)"}`,
                    background:sel?"var(--blue-100)":"#fff",
                    color:sel?"var(--blue-700)":"var(--gray-500)",
                    fontFamily:"inherit", fontSize:".8rem", fontWeight:sel?700:400,
                    transition:"var(--t)",
                  }}>
                    {sel && "✓ "}{label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!editTc && form.name && form.email && form.password && (
          <div style={{ marginTop:10, background:"var(--green-50)", border:"1px solid var(--green-100)", borderRadius:"var(--r8)", padding:"12px 14px" }}>
            <div style={{ fontSize:".78rem", fontWeight:700, color:"var(--green-600)", marginBottom:6 }}>📋 Credentials to share with teacher:</div>
            <div style={{ fontSize:".82rem", fontFamily:"monospace", lineHeight:1.8, color:"var(--gray-700)" }}>
              <div>🌐 URL: <strong>vidyatrack3.web.app</strong></div>
              <div>📧 Email: <strong>{form.email}</strong></div>
              <div>🔑 Password: <strong>{form.password}</strong></div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
